import { Paper, Tabs } from "@mantine/core";
import { useNavigate, useParams } from "react-router";
import type { Frequency } from "../../../utils/enums";
import { PageContent } from "../../BuildingBlocks/Layout/PageContent/PageContent";
import { PageHeader } from "../../BuildingBlocks/Layout/PageHeader/PageHeader";
import { EnergyDataIndicator } from "../../Menu/MenuItem/EnergyDataIndicator";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { AusfallarbeitPage } from "./AusfallarbeitPage/AusfallarbeitPage";
import { EnergyDataAvailability } from "./EnergyDataAvailability/EnergyDataAvailability";
import { EnergyDataElectricityTax } from "./EnergyDataElectricityTax/EnergyDataElectricityTax";
import { EnergyDataPage } from "./EnergyDataPage/EnergyDataPage";
import { EnergyDataSteuersignale } from "./EnergyDataSteuersignale/EnergyDataSteuersignale";
import { EnergyDataUploadPage } from "./EnergyDataUploadPage/EnergyDataUploadPage";
import "./EnergyDataView.scss";
import { MachineAccountPage } from "./MachineAccountPage/MachineAccountPage";
import { RawEnergyDataPage } from "./RawEnergyDataPage/RawEnergyDataPage";
import { ThirdPartySystemsTabContent } from "./ThirdPartySystemsTabContent/ThirdPartySystemsTabContent";

export const TABS = {
  TAB_ENERGY_DATA_AVAILABILITY: "verfuegbarkeit",
  TAB_ELECTRICITY_TAX: "stromsteuer",
  TAB_ENERGY_DATA_UPLOAD: "bereitstellen",
  TAB_THIRD_PARTY_SYSTEMS: "untermesssysteme",
  TAB_RAW_ENERGY_DATA: "zaehlerdaten",
  TAB_AUSFALLARBEIT: "ausfallarbeit",
  TAB_ENERGY_DATA: "berechnete-energiedaten",
  TAB_MACHINE_ACCOUNT: "machine-account",
  TAB_STEUERSIGNALE: "steuersignale"
} as const;

export const DEFAULT_ENERGY_DATA_SUBPAGE = TABS.TAB_ENERGY_DATA_AVAILABILITY;

export interface Acquisition {
  components: Array<string>;
  connections: Array<string>;
  detailLabel: string;
  frequency: Frequency;
  id: number;
  label: string;
  medium: string;
  origin: string;
  publicId: number | null;
  site: string;
  statement: string | null;
  tags: Array<Tag>;
  type: string;
}

export interface Tag {
  color: string;
  fontColor: string;
  longName: string;
  name: string;
}

export interface AcquisitionFilter {
  options: Array<string | { min: number; max: number }>;
  type: string;
}

export interface EnergyDataViewProps {
  variantId: number;
}

function EnergyDataView({ variantId }: EnergyDataViewProps) {
  const navigate = useNavigate();
  const routerParams = useParams();
  const { shouldShowStaffView } = useShouldShowStaffView();

  return (
    <PageContent className="EnergyDataView">
      <PageHeader title="Energiedaten" />
      <Paper>
        <Tabs
          className="energydata-tabs"
          keepMounted={false}
          value={routerParams.tab || DEFAULT_ENERGY_DATA_SUBPAGE}
          onChange={(value) => {
            let navTarget = `../${value}`;

            // navigating from a nested subpage needs to add one extra `../` to the relative path
            if (
              routerParams.tab &&
              !routerParams["*"]?.includes(routerParams.tab)
            ) {
              navTarget = `../${navTarget}`;
            }

            navigate(navTarget);
          }}
        >
          <Tabs.List mb="lg">
            <Tabs.Tab value={TABS.TAB_ENERGY_DATA_AVAILABILITY}>
              Verfügbarkeit
            </Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_ELECTRICITY_TAX}>Stromsteuer</Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_ENERGY_DATA_UPLOAD}>
              Bereitstellen
            </Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_THIRD_PARTY_SYSTEMS}>
              Untermesssysteme
            </Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_MACHINE_ACCOUNT}>
              Machine Account
            </Tabs.Tab>
            <Tabs.Tab
              rightSection={
                <EnergyDataIndicator scope="meters" variantId={variantId} />
              }
              value={TABS.TAB_RAW_ENERGY_DATA}
            >
              Zähler- und Lokationsdaten
            </Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_AUSFALLARBEIT}>Ausfallarbeit</Tabs.Tab>
            <Tabs.Tab value={TABS.TAB_ENERGY_DATA}>
              Berechnete Energiedaten
            </Tabs.Tab>
            {shouldShowStaffView && (
              <Tabs.Tab value={TABS.TAB_STEUERSIGNALE}>
                Steuersignale bereitstellen
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value={TABS.TAB_ENERGY_DATA_AVAILABILITY}>
            <EnergyDataAvailability variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_ELECTRICITY_TAX}>
            <EnergyDataElectricityTax variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_ENERGY_DATA_UPLOAD}>
            <EnergyDataUploadPage variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_THIRD_PARTY_SYSTEMS}>
            <ThirdPartySystemsTabContent variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_MACHINE_ACCOUNT}>
            <MachineAccountPage variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_RAW_ENERGY_DATA}>
            <RawEnergyDataPage variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_AUSFALLARBEIT}>
            <AusfallarbeitPage />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_ENERGY_DATA}>
            <EnergyDataPage variantId={variantId} />
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_STEUERSIGNALE}>
            <EnergyDataSteuersignale />
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </PageContent>
  );
}

export { EnergyDataView };
